import { InternalUser } from "../types/app";

export interface BlacklistProperty {
  id: number;
  address: string;
  unit: string;
  city: string;
  zip: string;
  section: string;
  block: string;
  subdivision: string;
  stateId?: number;
  countyId?: number;
  latitude?: number;
  longitude?: number;
  geocodedAddress: string;
  notes: string;

  state?: State;
  county?: County;

  bulletins: PropertyBulletin[];
  parcels: Parcel[];
  lots: Lot[];
}

export interface BlacklistNameEntity {
  name: string;
  companyTypeId: DropdownOption;
  taxID: string;
  notes: string;
  lookupCode: string;
}
export interface BlacklistNameIndividual {
  title: string;
  firstName: string;
  middleName: string;
  lastName: string;
  suffix: string;
  taxID: string;
  isAttorney: boolean;
  isNotary: boolean;
  yearAdmittedToBar: string;
  notes: string;
  lookupCode: string;
}
export interface BulletinInformation {
  state: State;
  county: County;
  note: string;
}

export interface UploadInfo {
  underwriter: DropdownOption;
  date: any;
  number: string;
  file: File;
}
export interface DropdownOption {
  value: number | null;
  label: string;
}

export interface State {
  id: number;
  name: string;
  abbreviation: string;
  counties: County[];
}

export interface County {
  id: number;
  stateId: number;
  name: string;
  state: State;
}
export interface PropertyErrors {
  state?: string;
  city?: string;
  address?: string;
  parcelId?: string;
  block?: string;
  lot?: string;
  section?: string;
  condoNumber?: string;
}

export interface BaseEntity {
  isActive: boolean;
  createdDate?: string;
  createdBy?: string;
  createdByUser?: InternalUser;
}

export interface RecordEntity extends BaseEntity {
  id: number;
  notes?: string;
}

export interface PersonRecord extends RecordEntity {
  title?: string;
  firstName: string;
  middleName?: string;
  lastName: string;
  suffix?: string;
  taxID?: string;
  lookupCode?: string;
  isAttorney: boolean;
  isNotary: boolean;
  yearAdmittedToBar?: string;
  bulletins: PersonBulletin[];
  removalRequest: RemovalRequest | null;
}

export interface CompanyRecord extends RecordEntity {
  name: string;
  companyTypeId?: number;
  taxID?: string;
  lookupCode?: string;
  companyType?: CompanyType;
  bulletins: CompanyBulletin[];
  removalRequest: RemovalRequest | null;
}

export interface PropertyRecord extends RecordEntity {
  address: string;
  unit?: string;
  city: string;
  zip?: string;
  section?: string;
  block?: string;
  subdivision?: string;
  stateId?: number;
  countyId?: number;
  latitude?: number;
  longitude?: number;
  geocodedAddress?: string;
  state?: State;
  county?: County;
  bulletins: PropertyBulletin[];
  parcels: Parcel[];
  lots: Lot[];
  removalRequest: RemovalRequest | null;
}

export interface Lot {
  id: number;
  propertyRecordId: number;
  lotNumber: string;
}

export interface Parcel {
  id: number;
  propertyRecordId: number;
  parcelId: string;
}

export interface CompanyBulletin {
  companyRecordId: number;
  bulletinId: number;
  bulletin: Bulletin;
}

export interface Bulletin {
  id: number;
  number: string;
  date?: Date;
  underwriterId: number;
  url: string;
  underwriter: Underwriter;
  locations?: BulletinLocation[];
  personBulletins?: PersonBulletin[];
  companyBulletins?: CompanyBulletin[];
  propertyBulletins?: PropertyBulletin[];
}

export interface PersonBulletin {
  personRecordId: number;
  bulletinId: number;
  bulletin: Bulletin;
}

export interface PropertyBulletin {
  propertyRecordId: number;
  bulletinId: number;
  bulletin: Bulletin;
}

export interface CompanyType {
  id: number;
  name: string;
}

export interface BulletinLocation {
  id: number;
  bulletinId: number;
  countyId?: number;
  stateId?: number;
  bulletin?: Bulletin;
  county?: County;
  state?: State;
}

export interface Underwriter {
  id: number;
  name: string;
}

export enum BlacklistEntityType {
  Person = 1,
  Company = 2,
  Property = 3
}

export enum RemovalRequestStatus {
  Pending,
  Approved,
  Rejected
}

export enum BlacklistMatchResolutionRequestStatus {
  Pending,
  Approved,
  Rejected
}

export interface RemovalRequest {
  entityType: BlacklistEntityType;
  entityId: number;
  requestedBy: string;
  requestedDate: Date;
  status: RemovalRequestStatus;
  reviewedBy: string | null;
  reviewedDate: Date | null;
  reason: string;
  proofUrl: string;

  reviewedByUser?: InternalUser;
  requestedByUser?: InternalUser;
}
export interface RemovalRequestDto {
  entityType: BlacklistEntityType;
  entityId: number;
  reason: string;
  proofDocumentBase64: string;
  proofDocumentFileName: string;
}
export interface CreateRemovalRequestDto {
  removalRequest: RemovalRequest;
  proofDocumentBase64: string;
  proofDocumentFileName: string;
}

export interface BlacklistMatchResolutionRequest {
  id: number;
  lookupCode: string;
  entityType: BlacklistEntityType;
  entityId: number;
  status: BlacklistMatchResolutionRequestStatus;
  requestedBy: string;
  requestedDate: Date;
  reviewedBy: string | null;
  reviewedDate: Date | null;
  reason: string;
}

export interface CreateBlacklistMatchResolutionRequestDto {
  lookupCode: string;
  entityType: BlacklistEntityType;
  entityId: number;
  reason: string;
}

export interface BlacklistMigrationReviewRow {
  id: number;
  sourceBlacklistId?: number;
  fieldName?: string;
  information?: string;
  informationToUseForImport?: string;
  underwriter?: string;
  county?: string;
  city?: string;
  state?: string;
  message?: string;
  notes?: string;
  bulletinDate?: string;
  bulletinNum?: string;
  bulletinLocation?: string;
  bulletinLocation2?: string;
  bulletinLocation3?: string;
  extractedFile1?: string;
  extractedFile2?: string;
  extractedFile3?: string;
  matchedBulletinId1?: number;
  matchedBulletinId2?: number;
  matchedBulletinId3?: number;
  exclusionReason?: string;
  manualReviewReason?: string;
  confirmedFieldName?: string;
  suspectedFieldName?: string;
  companyTypeCode?: number;
  unit?: string;
  section?: string;
  block?: string;
  lot?: string;
  rawRowJson?: string;
  targetEntityType?: BlacklistEntityType;
  targetRecordId?: number;
  imported: boolean;
  reviewNote?: string;
  commitError?: string;
  lookupCode?: string;
  taxId?: string;
  personTitle?: string;
  personFirstName?: string;
  personMiddleName?: string;
  personLastName?: string;
  personSuffix?: string;
  personIsAttorney: boolean;
  personIsNotary: boolean;
  personYearAdmittedToBar?: string;
  companyName?: string;
  companyTypeId?: number;
  propertyAddress?: string;
  propertyUnit?: string;
  propertyCity?: string;
  propertyZip?: string;
  propertySection?: string;
  propertyBlock?: string;
  propertySubdivision?: string;
  propertyStateId?: number;
  propertyCountyId?: number;
  propertyParcelId?: string;
  propertyLot?: string;
}

export interface BlacklistMigrationReviewResponse {
  count: number;
  data: BlacklistMigrationReviewRow[];
}

export interface BlacklistMigrationUploadResult {
  imported: number;
  skippedDuplicates: number;
}
